import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState, } from 'react';
import { Now } from 'temporal-luxon';
import { isPtsOnlyConsentDocument, normalizeDocumentTitle } from 'libs.consent.common';
import { FirebaseAuthContext } from 'libs.firebase_react';
import { defaultConsentActions } from './consent.context.types';
import { consentReducer, initialState } from './consent.reducer';
import { useConsentLibrary } from '../hooks/use_consent_library.hook';
import { carryForwardElements } from '../utils/carry_forward_elements';
import { configurationDataAuditAuthor, elementCustomDataFingerprint, withConfigurationDataAuditEntry, } from '../utils/configuration_data_audit';
import { TEMPLATE_DATA_DAMAGED_MESSAGE } from '../utils/consent_publish_validation';
import { isDuplicateConsentTemplateNameError } from '../utils/library_error';
export const ConsentStateContext = createContext({
    state: initialState,
    activeDocument: null,
    selectedElement: null,
    consentFiles: [],
    isLoading: true,
    templateTotal: 0,
    publishingTemplateIds: new Set(),
});
export const ConsentActionsContext = createContext(defaultConsentActions);
export const ConsentScopeContext = createContext({
    studyId: undefined,
});
const readFileAsDataUrl = (blob) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(blob);
});
const persistDocumentData = (document, trail, persistName = true) => {
    const name = persistName ? normalizeDocumentTitle(document.name) : '';
    return {
        elements: document.elements,
        totalPages: document.totalPages,
        roles: document.roles,
        customFields: document.customFields,
        configurationDataAudit: trail,
        // Written under the Cortex field names because MOM reads the published
        // Library resource's `data` directly when it projects to the shell and pin.
        ec__instruction_screen: document.instructionScreen ?? '',
        ec__completion_screen: document.completionScreen ?? '',
        ...(name ? { name } : {}),
        description: document.description ?? '',
        ...(document.language ? { ec__language: document.language } : {}),
        ...(document.carryForward ? { carryForward: document.carryForward } : {}),
        ec__delayed_signing: isPtsOnlyConsentDocument(document) ? false : document.delayedSigning === true,
        ...(document.sourceLabel !== undefined ? { sourceLabel: document.sourceLabel } : {}),
        ...(document.sourceVersion !== undefined ? { sourceVersion: document.sourceVersion } : {}),
        ...(document.sourceVersionDate !== undefined ? { sourceVersionDate: document.sourceVersionDate } : {}),
        ...(document.sourceFileName !== undefined ? { sourceFileName: document.sourceFileName } : {}),
    };
};
// A damaged document holds only the roles Studio could read, so any write would replace the stored
// record the bundler refuses, and the evidence support needs, with that filtered list.
const damagedDocumentWriteError = () => new Error(TEMPLATE_DATA_DAMAGED_MESSAGE);
export const ConsentProvider = ({ entityId, studyId, children }) => {
    const firebaseAuth = useContext(FirebaseAuthContext);
    const [state, dispatch] = useReducer(consentReducer, initialState);
    const documentsRef = useRef(state.documents);
    documentsRef.current = state.documents;
    const persistQueueRef = useRef(Promise.resolve());
    const [consentFiles, setConsentFiles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [templateTotal, setTemplateTotal] = useState(0);
    const [publishingTemplateIds, setPublishingTemplateIds] = useState(() => new Set());
    const loadDocumentsSeq = useRef(0);
    const lastCustomDataFingerprintRef = useRef({});
    const lastPersistedNameByIdRef = useRef({});
    const rejectedNameByIdRef = useRef({});
    const { loadResources, loadAssignmentCatalog: loadAssignmentCatalogResources, getResource, createResource, updateResourceData, archiveResource, getArchiveImpact, deleteResource, cloneResourceWithFile, convertSourceFile, replaceDocumentAsset, clearDocumentAsset, getResourcePdfUrl, bundleConsentTemplate, publishTemplate, } = useConsentLibrary(entityId, studyId);
    const loadDocuments = useCallback(async (page = 1, query) => {
        const seq = ++loadDocumentsSeq.current;
        // Paging back over a cached family page should not blank the table. Show the
        // spinner only on the first load (isLoading starts true) or an explicit refresh.
        if (query?.refresh) {
            setIsLoading(true);
        }
        try {
            const { fileRows, documents, total = 0 } = await loadResources(page, query);
            if (seq !== loadDocumentsSeq.current) {
                return;
            }
            dispatch({ type: 'LOAD_DOCUMENTS', payload: documents });
            documents.forEach((loaded) => {
                const persistedName = normalizeDocumentTitle(loaded.name);
                if (persistedName) {
                    lastPersistedNameByIdRef.current[loaded.id] = persistedName;
                }
            });
            setConsentFiles(fileRows);
            setTemplateTotal(total);
        }
        finally {
            if (seq === loadDocumentsSeq.current) {
                setIsLoading(false);
            }
        }
    }, [loadResources]);
    const loadAssignmentCatalog = useCallback(async (refresh = false) => {
        const { fileRows } = await loadAssignmentCatalogResources(refresh);
        return fileRows;
    }, [loadAssignmentCatalogResources]);
    const hydrateDocument = useCallback(async (id) => {
        const { fileRow, document } = await getResource(id);
        lastCustomDataFingerprintRef.current[document.id] = elementCustomDataFingerprint(document.elements);
        const persistedName = normalizeDocumentTitle(document.name);
        if (persistedName) {
            lastPersistedNameByIdRef.current[document.id] = persistedName;
        }
        dispatch({ type: 'UPDATE_DOCUMENT', payload: document });
        setConsentFiles((prev) => prev.map((file) => (file.id === id ? { ...file, ...fileRow } : file)));
        return document;
    }, [getResource]);
    const persistAndDispatch = useCallback((document, type) => {
        if (type === 'UPDATE_DOCUMENT' &&
            (document.storedDataInvalid ||
                documentsRef.current.some((candidate) => candidate.id === document.id && candidate.storedDataInvalid))) {
            return Promise.reject(damagedDocumentWriteError());
        }
        if (type === 'UPDATE_DOCUMENT' && document.totalPages > 0) {
            documentsRef.current = documentsRef.current.map((candidate) => candidate.id === document.id ? { ...candidate, totalPages: document.totalPages } : candidate);
            dispatch({
                type: 'MERGE_DOCUMENT',
                payload: { documentId: document.id, changes: { totalPages: document.totalPages } },
            });
        }
        const run = async () => {
            const current = documentsRef.current.find((candidate) => candidate.id === document.id) ?? document;
            const confirming = Boolean(document.carryForward?.placementsConfirmed) && !current.carryForward?.placementsConfirmed;
            const argumentUnconfirms = Boolean(current.carryForward?.placementsConfirmed) && document.carryForward?.placementsConfirmed === false;
            const nextCarryForward = argumentUnconfirms
                ? current.carryForward
                : (document.carryForward ?? current.carryForward);
            let nextElements = document.elements;
            if (confirming) {
                nextElements = current.elements.map((element) => element.needsDimensionConfirmation ? { ...element, needsDimensionConfirmation: false } : element);
            }
            else if (argumentUnconfirms) {
                nextElements = current.elements;
            }
            const toPersist = type === 'ADD_DOCUMENT'
                ? document
                : {
                    ...current,
                    ...document,
                    totalPages: document.totalPages > 0 ? document.totalPages : current.totalPages,
                    elements: nextElements,
                    ...(nextCarryForward ? { carryForward: nextCarryForward } : {}),
                };
            const nextFingerprint = elementCustomDataFingerprint(toPersist.elements);
            const trail = withConfigurationDataAuditEntry(lastCustomDataFingerprintRef.current[toPersist.id], nextFingerprint, toPersist.configurationDataAudit ?? [], configurationDataAuditAuthor(firebaseAuth?.user), Now.instant().toString());
            const normalizedName = normalizeDocumentTitle(toPersist.name);
            // A 409 on a rename leaves the optimistic title in this snapshot. Later field
            // saves must not resend that title or they 409 too and drop the field edit.
            const persistName = Boolean(normalizedName) && rejectedNameByIdRef.current[toPersist.id] !== normalizedName;
            const persist = async () => {
                try {
                    await updateResourceData(toPersist.id, persistDocumentData(toPersist, trail, persistName));
                }
                catch (error) {
                    if (isDuplicateConsentTemplateNameError(error)) {
                        if (normalizedName) {
                            rejectedNameByIdRef.current[toPersist.id] = normalizedName;
                        }
                        const lastName = lastPersistedNameByIdRef.current[toPersist.id];
                        if (lastName) {
                            dispatch({ type: 'UPDATE_DOCUMENT_NAME', payload: { documentId: toPersist.id, name: lastName } });
                            setConsentFiles((prev) => prev.map((file) => (file.id === toPersist.id ? { ...file, title: lastName } : file)));
                        }
                    }
                    throw error;
                }
                if (persistName && normalizedName) {
                    lastPersistedNameByIdRef.current[toPersist.id] = normalizedName;
                    delete rejectedNameByIdRef.current[toPersist.id];
                }
            };
            if (type === 'ADD_DOCUMENT') {
                await persist();
                lastCustomDataFingerprintRef.current[toPersist.id] = nextFingerprint;
                dispatch({ type, payload: { ...toPersist, configurationDataAudit: trail } });
                return;
            }
            let confirmChanges;
            if (confirming) {
                confirmChanges = {
                    ...(toPersist.carryForward ? { carryForward: toPersist.carryForward } : {}),
                    elements: nextElements,
                };
            }
            else if (argumentUnconfirms) {
                confirmChanges = { carryForward: current.carryForward };
            }
            if (confirmChanges) {
                documentsRef.current = documentsRef.current.map((candidate) => candidate.id === toPersist.id ? { ...candidate, ...confirmChanges } : candidate);
                dispatch({
                    type: 'MERGE_DOCUMENT',
                    payload: { documentId: toPersist.id, changes: confirmChanges },
                });
            }
            try {
                await persist();
            }
            catch (error) {
                if (confirming) {
                    documentsRef.current = documentsRef.current.map((candidate) => candidate.id === current.id
                        ? { ...candidate, carryForward: current.carryForward, elements: current.elements }
                        : candidate);
                    dispatch({
                        type: 'MERGE_DOCUMENT',
                        payload: {
                            documentId: current.id,
                            changes: {
                                elements: current.elements,
                                carryForward: current.carryForward,
                            },
                        },
                    });
                }
                throw error;
            }
            lastCustomDataFingerprintRef.current[toPersist.id] = nextFingerprint;
            dispatch({
                type: 'MERGE_DOCUMENT',
                payload: {
                    documentId: toPersist.id,
                    changes: { configurationDataAudit: trail },
                },
            });
        };
        const pending = persistQueueRef.current.then(run, run);
        persistQueueRef.current = pending.then(() => undefined, () => undefined);
        return pending;
    }, [firebaseAuth, updateResourceData]);
    const addDocument = useCallback((document) => persistAndDispatch(document, 'ADD_DOCUMENT'), [persistAndDispatch]);
    const updateDocument = useCallback((document) => persistAndDispatch(document, 'UPDATE_DOCUMENT'), [persistAndDispatch]);
    const markResourceArchived = useCallback((id) => {
        dispatch({ type: 'ARCHIVE_DOCUMENT', payload: id });
        setConsentFiles((prev) => prev.map((file) => (file.id === id ? { ...file, status: 'Archived' } : file)));
    }, []);
    const setActiveDocument = useCallback((documentId) => {
        dispatch({ type: 'SET_ACTIVE_DOCUMENT', payload: documentId });
    }, []);
    const addElement = useCallback((documentId, element) => {
        dispatch({ type: 'ADD_ELEMENT', payload: { documentId, element } });
    }, []);
    const autoMapElements = useCallback((documentId, elements) => {
        dispatch({ type: 'AUTO_MAP_ELEMENTS', payload: { documentId, elements } });
    }, []);
    const updateElement = useCallback((documentId, element) => {
        dispatch({ type: 'UPDATE_ELEMENT', payload: { documentId, element } });
    }, []);
    const deleteElement = useCallback((documentId, elementId) => {
        dispatch({ type: 'DELETE_ELEMENT', payload: { documentId, elementId } });
    }, []);
    const setSelectedElement = useCallback((elementId) => {
        dispatch({ type: 'SET_SELECTED_ELEMENT', payload: elementId });
    }, []);
    const updateDocumentRoles = useCallback((documentId, roles) => {
        dispatch({ type: 'UPDATE_DOCUMENT_ROLES', payload: { documentId, roles } });
    }, []);
    const updateDocumentLanguage = useCallback((documentId, language) => {
        dispatch({ type: 'UPDATE_DOCUMENT_LANGUAGE', payload: { documentId, language } });
        setConsentFiles((prev) => prev.map((file) => (file.id === documentId ? { ...file, language } : file)));
    }, []);
    const updateDocumentDelayedSigning = useCallback((documentId, delayedSigning) => {
        dispatch({ type: 'UPDATE_DOCUMENT_DELAYED_SIGNING', payload: { documentId, delayedSigning } });
    }, []);
    const updateDocumentName = useCallback((documentId, name) => {
        delete rejectedNameByIdRef.current[documentId];
        dispatch({ type: 'UPDATE_DOCUMENT_NAME', payload: { documentId, name } });
        setConsentFiles((prev) => prev.map((file) => (file.id === documentId ? { ...file, title: name } : file)));
    }, []);
    const updateDocumentDescription = useCallback((documentId, description) => {
        dispatch({ type: 'UPDATE_DOCUMENT_DESCRIPTION', payload: { documentId, description } });
        setConsentFiles((prev) => prev.map((file) => (file.id === documentId ? { ...file, description } : file)));
    }, []);
    const updateDocumentCustomFields = useCallback((documentId, customFields) => {
        dispatch({ type: 'UPDATE_DOCUMENT_CUSTOM_FIELDS', payload: { documentId, customFields } });
    }, []);
    const updateDocumentScreens = useCallback((documentId, screens) => {
        dispatch({ type: 'UPDATE_DOCUMENT_SCREENS', payload: { documentId, ...screens } });
    }, []);
    const updateDocumentSource = useCallback((documentId, source) => {
        dispatch({ type: 'UPDATE_DOCUMENT_SOURCE', payload: { documentId, ...source } });
    }, []);
    const clearDocumentSourceFile = useCallback(async (documentId) => {
        if (documentsRef.current.some((doc) => doc.id === documentId && doc.storedDataInvalid)) {
            throw damagedDocumentWriteError();
        }
        await clearDocumentAsset(documentId);
        dispatch({
            type: 'UPDATE_DOCUMENT_SOURCE',
            payload: { documentId, assetId: '', sourceFileName: '', pdfDataUrl: '' },
        });
    }, [clearDocumentAsset]);
    const saveDocumentToStorage = updateDocument;
    const eagerLoadDocumentPdf = useCallback(async (documentId, assetId) => {
        try {
            const pdfUrl = await getResourcePdfUrl(assetId);
            if (!pdfUrl) {
                return undefined;
            }
            const response = await fetch(pdfUrl);
            if (response.ok === false) {
                return undefined;
            }
            const blob = await response.blob();
            const dataUrl = await readFileAsDataUrl(blob);
            dispatch({ type: 'SET_DOCUMENT_PDF', payload: { documentId, pdfDataUrl: dataUrl } });
            return dataUrl;
        }
        catch {
            // The Library resource already exists; opening the editor can fetch the PDF later.
            return undefined;
        }
    }, [getResourcePdfUrl]);
    const replaceDocumentSourceFile = useCallback(async (documentId, file) => {
        const current = documentsRef.current.find((doc) => doc.id === documentId);
        if (!current) {
            throw new Error('Document not found');
        }
        if (current.storedDataInvalid) {
            throw damagedDocumentWriteError();
        }
        const { readPdfPageMetrics } = await import('../utils/pdf_page_metrics');
        let parentMetrics = { pageCount: 0, sizes: new Map() };
        const parentSource = current.pdfDataUrl || (current.assetId ? await getResourcePdfUrl(current.assetId) : '');
        if (parentSource) {
            try {
                parentMetrics = await readPdfPageMetrics(parentSource);
            }
            catch {
                parentMetrics = { pageCount: 0, sizes: new Map() };
            }
        }
        // Convert (if DocX) and measure the local render before any Library write: a failed convert
        // leaves the previous render in place, and the asset and its geometry are saved in one PUT.
        const renderFile = await convertSourceFile(file);
        const hasElements = current.elements.length > 0;
        let { elements } = current;
        let { totalPages } = current;
        let { carryForward } = current;
        try {
            const newMetrics = await readPdfPageMetrics(renderFile);
            totalPages = newMetrics.pageCount;
            if (hasElements) {
                elements = carryForwardElements(current.elements, newMetrics.pageCount, parentMetrics.sizes, newMetrics.sizes);
                carryForward = {
                    fromResourceId: current.carryForward?.fromResourceId ?? current.id,
                    fromVersion: current.carryForward?.fromVersion ?? 0,
                    fromName: current.carryForward?.fromName ?? current.name,
                    carriedAt: new Date().toISOString(),
                    placementsConfirmed: false,
                };
            }
        }
        catch {
            if (hasElements) {
                elements = [];
                totalPages = 0;
                carryForward = {
                    fromResourceId: current.carryForward?.fromResourceId ?? current.id,
                    fromVersion: current.carryForward?.fromVersion ?? 0,
                    fromName: current.carryForward?.fromName ?? current.name,
                    carriedAt: new Date().toISOString(),
                    placementsConfirmed: true,
                    failed: true,
                };
            }
            else {
                totalPages = 0;
            }
        }
        const measuredDocument = {
            ...current,
            sourceFileName: renderFile.name,
            pdfDataUrl: '',
            totalPages,
            elements,
            ...(carryForward ? { carryForward } : {}),
        };
        const assetId = await replaceDocumentAsset(documentId, renderFile, persistDocumentData(measuredDocument, current.configurationDataAudit ?? []));
        const nextDocument = { ...measuredDocument, assetId };
        documentsRef.current = documentsRef.current.map((doc) => (doc.id === documentId ? nextDocument : doc));
        dispatch({
            type: 'UPDATE_DOCUMENT_SOURCE',
            payload: { documentId, assetId, sourceFileName: renderFile.name, pdfDataUrl: '' },
        });
        dispatch({
            type: 'MERGE_DOCUMENT',
            payload: { documentId, changes: { totalPages, elements, ...(carryForward ? { carryForward } : {}) } },
        });
        await eagerLoadDocumentPdf(documentId, assetId);
    }, [convertSourceFile, eagerLoadDocumentPdf, getResourcePdfUrl, replaceDocumentAsset]);
    const uploadConsentFile = useCallback(async (file, name, metadata) => {
        const { fileRow, document } = await createResource(file, name, metadata);
        lastCustomDataFingerprintRef.current[document.id] = elementCustomDataFingerprint(document.elements);
        const persistedName = normalizeDocumentTitle(document.name);
        if (persistedName) {
            lastPersistedNameByIdRef.current[document.id] = persistedName;
        }
        dispatch({ type: 'ADD_DOCUMENT', payload: document });
        setConsentFiles((prev) => [...prev, fileRow]);
        if (document.assetId) {
            const pdfDataUrl = await eagerLoadDocumentPdf(document.id, document.assetId);
            if (pdfDataUrl) {
                return { ...document, pdfDataUrl };
            }
        }
        return document;
    }, [createResource, eagerLoadDocumentPdf]);
    const archiveTemplateVersion = useCallback(async (id) => {
        const templateKey = consentFiles.find((file) => file.id === id)?.templateKey ?? state.documents.find((doc) => doc.id === id)?.key;
        // mark archived only after the API call succeeds (matches publishConsentTemplate's
        // pattern below) — marking it first meant a failed/rejected archiveResource call left the
        // UI showing "Archived" permanently with no rollback and no visible error.
        await archiveResource(templateKey ?? '', id);
        markResourceArchived(id);
    }, [archiveResource, consentFiles, markResourceArchived, state.documents]);
    const cloneTemplateVersion = useCallback(async (id, options) => {
        const { fileRow, document } = await cloneResourceWithFile(id, {
            ...options,
            auditAuthor: options.auditAuthor ?? configurationDataAuditAuthor(firebaseAuth?.user),
        });
        lastCustomDataFingerprintRef.current[document.id] = elementCustomDataFingerprint(document.elements);
        const persistedName = normalizeDocumentTitle(document.name);
        if (persistedName) {
            lastPersistedNameByIdRef.current[document.id] = persistedName;
        }
        dispatch({ type: 'ADD_DOCUMENT', payload: document });
        setConsentFiles((prev) => [...prev, fileRow]);
        // Eagerly resolve the new PDF here rather than relying on handleEditRow's own
        // state.documents lookup, which reads a closure not guaranteed to see this dispatch yet —
        // this is what lets "Save" open the editor with the new PDF already loaded, not blank.
        if (document.assetId) {
            await eagerLoadDocumentPdf(document.id, document.assetId);
        }
        return document;
    }, [cloneResourceWithFile, eagerLoadDocumentPdf, firebaseAuth]);
    const deleteTemplateVersion = useCallback(async (id) => {
        await deleteResource(id);
        dispatch({ type: 'DELETE_DOCUMENT', payload: id });
        setConsentFiles((prev) => prev.filter((file) => file.id !== id));
    }, [deleteResource]);
    const fetchDocumentPdf = useCallback(async (documentId, assetId) => {
        const document = state.documents.find((d) => d.id === documentId);
        const resolvedAssetId = assetId || document?.assetId;
        if (!resolvedAssetId) {
            return undefined;
        }
        // eagerLoadDocumentPdf swallows every failure mode, so its resolved value is the only
        // signal a caller has that the PDF is actually available to render.
        return eagerLoadDocumentPdf(documentId, resolvedAssetId);
    }, [eagerLoadDocumentPdf, state.documents]);
    const saveConsentTemplate = useCallback(async (document) => {
        // Persist latest editor state so consent_bundler compiles from Library, not stale data.
        await persistAndDispatch(document, 'UPDATE_DOCUMENT');
        await bundleConsentTemplate(document.id);
    }, [bundleConsentTemplate, persistAndDispatch]);
    const publishConsentTemplate = useCallback(async (document) => {
        await publishTemplate(document);
        setConsentFiles((prev) => prev.map((file) => (file.id === document.id ? { ...file, status: 'Published' } : file)));
    }, [publishTemplate]);
    // Publish is a chain of browser requests, so the author can leave the editor while it runs but
    // a reload would stop it partway and strand the template in Draft or In Review.
    const trackTemplatePublish = useCallback(async (documentId, run) => {
        setPublishingTemplateIds((current) => new Set(current).add(documentId));
        try {
            await run();
        }
        finally {
            setPublishingTemplateIds((current) => {
                const next = new Set(current);
                next.delete(documentId);
                return next;
            });
        }
    }, []);
    useEffect(() => {
        if (publishingTemplateIds.size === 0) {
            return;
        }
        const warnWhilePublishing = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warnWhilePublishing);
        return () => window.removeEventListener('beforeunload', warnWhilePublishing);
    }, [publishingTemplateIds]);
    const activeDocument = useMemo(() => state.documents.find((d) => d.id === state.activeDocumentId) ?? null, [state.activeDocumentId, state.documents]);
    const selectedElement = useMemo(() => activeDocument?.elements.find((el) => el.id === state.selectedElementId) ?? null, [activeDocument, state.selectedElementId]);
    const stateContextValue = useMemo(() => ({
        state,
        activeDocument,
        selectedElement,
        consentFiles,
        isLoading,
        templateTotal,
        publishingTemplateIds,
    }), [state, activeDocument, selectedElement, consentFiles, isLoading, templateTotal, publishingTemplateIds]);
    const actionsContextValue = useMemo(() => ({
        loadDocuments,
        loadAssignmentCatalog,
        hydrateDocument,
        addDocument,
        updateDocument,
        setActiveDocument,
        addElement,
        autoMapElements,
        updateElement,
        deleteElement,
        setSelectedElement,
        updateDocumentRoles,
        updateDocumentLanguage,
        updateDocumentDelayedSigning,
        updateDocumentName,
        updateDocumentDescription,
        updateDocumentCustomFields,
        updateDocumentScreens,
        updateDocumentSource,
        replaceDocumentSourceFile,
        clearDocumentSourceFile,
        saveDocumentToStorage,
        uploadConsentFile,
        archiveTemplateVersion,
        getArchiveImpact,
        cloneTemplateVersion,
        deleteTemplateVersion,
        fetchDocumentPdf,
        saveConsentTemplate,
        publishConsentTemplate,
        trackTemplatePublish,
    }), [
        loadDocuments,
        loadAssignmentCatalog,
        hydrateDocument,
        addDocument,
        updateDocument,
        setActiveDocument,
        addElement,
        autoMapElements,
        updateElement,
        deleteElement,
        setSelectedElement,
        updateDocumentRoles,
        updateDocumentLanguage,
        updateDocumentDelayedSigning,
        updateDocumentName,
        updateDocumentDescription,
        updateDocumentCustomFields,
        updateDocumentScreens,
        updateDocumentSource,
        replaceDocumentSourceFile,
        clearDocumentSourceFile,
        saveDocumentToStorage,
        uploadConsentFile,
        archiveTemplateVersion,
        getArchiveImpact,
        cloneTemplateVersion,
        deleteTemplateVersion,
        fetchDocumentPdf,
        saveConsentTemplate,
        publishConsentTemplate,
        trackTemplatePublish,
    ]);
    const scopeContextValue = useMemo(() => ({ studyId }), [studyId]);
    return (_jsx(ConsentScopeContext.Provider, { value: scopeContextValue, children: _jsx(ConsentActionsContext.Provider, { value: actionsContextValue, children: _jsx(ConsentStateContext.Provider, { value: stateContextValue, children: children }) }) }));
};
